// An explicitly empty VITE_API_BASE means "same origin, relative paths" (production
// behind a single-origin reverse proxy) - must NOT fall back to the dev default, since
// "" is falsy and `||` would otherwise silently discard it.
export function resolveApiBase(raw: string | undefined): string {
  return raw !== undefined ? raw : 'http://localhost:8000';
}

const API_BASE = resolveApiBase(import.meta.env.VITE_API_BASE);

const OWNER_REPLY_OPERATION_STORAGE_PREFIX = 'fleet-hub.owner-reply.v1:';
const OWNER_REPLY_SESSION_STORAGE_KEY = 'fleet-hub.owner-reply-session.v1';
const AUTH_CONTEXT_STORAGE_KEY = 'fleet_hub_auth_context.v1';
const AUTH_STORAGE_UNAVAILABLE = Symbol('auth-storage-unavailable');

interface AuthContext {
  epoch: string;
  state: 'pending' | 'ready';
  credential?: string | null;
}

const initialStoredToken = readStoredToken();
let token: string | null = initialStoredToken === AUTH_STORAGE_UNAVAILABLE ? null : initialStoredToken;
let authGeneration = 0;
const initialAuthContext = readAuthContext();
let activeAuthEpoch: string | null = initialAuthContext !== AUTH_STORAGE_UNAVAILABLE && initialAuthContext?.state === 'ready' ? initialAuthContext.epoch : null;
let authContextUnavailable = false;
let requiresExplicitAuthentication = false;

function readStoredToken(): string | null | typeof AUTH_STORAGE_UNAVAILABLE {
  try {
    return localStorage.getItem('fleet_hub_token');
  } catch {
    return AUTH_STORAGE_UNAVAILABLE;
  }
}

function createOpaqueId(): string | null {
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

function readAuthContext(): AuthContext | null | typeof AUTH_STORAGE_UNAVAILABLE {
  let raw: string | null;
  try {
    raw = localStorage.getItem(AUTH_CONTEXT_STORAGE_KEY);
  } catch {
    return AUTH_STORAGE_UNAVAILABLE;
  }
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<AuthContext>;
    if (
      typeof value.epoch !== 'string' ||
      !/^[0-9a-f]{32}$/.test(value.epoch) ||
      (value.state !== 'pending' && value.state !== 'ready') ||
      (value.state === 'ready' && !(typeof value.credential === 'string' || value.credential === null))
    ) {
      return { epoch: '', state: 'pending' };
    }
    return value as AuthContext;
  } catch {
    return { epoch: '', state: 'pending' };
  }
}

function writeAuthContext(context: AuthContext): boolean {
  try {
    const serialized = JSON.stringify(context);
    localStorage.setItem(AUTH_CONTEXT_STORAGE_KEY, serialized);
    return localStorage.getItem(AUTH_CONTEXT_STORAGE_KEY) === serialized;
  } catch {
    return false;
  }
}

function invalidateLocalReplyNamespace() {
  clearOwnerReplyOperations();
  activeAuthEpoch = null;
}

function synchronizeAuthContext(): boolean {
  const context = readAuthContext();
  const storedCredential = readStoredToken();
  if (context === AUTH_STORAGE_UNAVAILABLE || storedCredential === AUTH_STORAGE_UNAVAILABLE) {
    // Unreadable storage is uncertainty, not proof of logout or a new owner.
    // Fence responses while retaining the exact unresolved key/body for recovery.
    if (!authContextUnavailable) {
      authGeneration += 1;
      window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
    }
    authContextUnavailable = true;
    token = null;
    return false;
  }
  if (!context) {
    if (activeAuthEpoch !== null) {
      authGeneration += 1;
      invalidateLocalReplyNamespace();
      requiresExplicitAuthentication = true;
      authContextUnavailable = true;
      token = null;
      window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
      window.dispatchEvent(new Event('fleet-hub-auth-context-changed'));
      return false;
    }
    if (requiresExplicitAuthentication) {
      authContextUnavailable = true;
      token = null;
      return false;
    }
    // Upgrade an installation that predates the shared auth marker. Existing
    // unbound reply sessions are rejected by getOwnerReplySessionId below.
    const epoch = createOpaqueId();
    const credential = storedCredential;
    if (!epoch || !writeAuthContext({ epoch, state: 'ready', credential })) {
      authContextUnavailable = true;
      token = null;
      return false;
    }
    activeAuthEpoch = epoch;
    authContextUnavailable = false;
    token = storedCredential;
    return true;
  }
  if (context.state !== 'ready' || !context.epoch) {
    if (!authContextUnavailable) {
      authGeneration += 1;
      clearOwnerReplyOperations();
      window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
      if (activeAuthEpoch !== null) window.dispatchEvent(new Event('fleet-hub-auth-context-changed'));
    }
    authContextUnavailable = true;
    requiresExplicitAuthentication = true;
    token = null;
    return false;
  }
  if (context.credential !== storedCredential) {
    // Older tabs can still write only fleet_hub_token. The ready context keeps
    // the prior credential so reloads detect that unversioned transition.
    if (!authContextUnavailable || !requiresExplicitAuthentication) {
      authGeneration += 1;
      invalidateLocalReplyNamespace();
      window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
      window.dispatchEvent(new Event('fleet-hub-auth-context-changed'));
    }
    authContextUnavailable = true;
    requiresExplicitAuthentication = true;
    token = null;
    return false;
  }

  // Only a successful explicit setToken may clear a failed-auth transition.
  if (requiresExplicitAuthentication) return false;

  if (activeAuthEpoch !== context.epoch) {
    const isCrossContextTransition = activeAuthEpoch !== null;
    authGeneration += 1;
    invalidateLocalReplyNamespace();
    activeAuthEpoch = context.epoch;
    requiresExplicitAuthentication = isCrossContextTransition;
    authContextUnavailable = isCrossContextTransition;
    token = isCrossContextTransition ? null : storedCredential;
    window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
    if (isCrossContextTransition) window.dispatchEvent(new Event('fleet-hub-auth-context-changed'));
  } else if (authContextUnavailable) {
    if (requiresExplicitAuthentication) return false;
    authContextUnavailable = false;
    token = storedCredential;
  } else if (token !== storedCredential) {
    // A token changed without the required epoch transition. Do not guess which
    // account owns pending replies; require a fresh explicit authentication.
    authGeneration += 1;
    invalidateLocalReplyNamespace();
    authContextUnavailable = true;
    requiresExplicitAuthentication = true;
    token = null;
    window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
    window.dispatchEvent(new Event('fleet-hub-auth-context-changed'));
    return false;
  }
  return !authContextUnavailable;
}

function clearOwnerReplyOperations() {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(OWNER_REPLY_OPERATION_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
    sessionStorage.removeItem(OWNER_REPLY_SESSION_STORAGE_KEY);
  } catch {
    // Authentication remains independent; reply sends fail closed if storage is unavailable.
  }
}

function createOwnerReplySessionId(epoch: string): string | null {
  const id = createOpaqueId();
  if (!id) return null;
  try {
    const binding = `${epoch}:${id}`;
    sessionStorage.setItem(OWNER_REPLY_SESSION_STORAGE_KEY, binding);
    return sessionStorage.getItem(OWNER_REPLY_SESSION_STORAGE_KEY) === binding ? id : null;
  } catch {
    return null;
  }
}

export function getOwnerReplySessionId(): string | null {
  if (!synchronizeAuthContext() || !token || !activeAuthEpoch) return null;
  try {
    const existing = sessionStorage.getItem(OWNER_REPLY_SESSION_STORAGE_KEY);
    const match = existing?.match(/^([0-9a-f]{32}):([0-9a-f]{32})$/);
    if (match && match[1] === activeAuthEpoch) return match[2];
  } catch {
    return null;
  }
  // An unbound or old-epoch namespace may contain an unresolved reply. Drop it
  // before creating a session id so it can never be replayed under this bearer.
  clearOwnerReplyOperations();
  return createOwnerReplySessionId(activeAuthEpoch);
}

export function setToken(next: string | null) {
  authGeneration += 1;
  token = null;
  authContextUnavailable = true;
  requiresExplicitAuthentication = true;
  activeAuthEpoch = null;
  const epoch = createOpaqueId();
  const failClosed = () => {
    invalidateLocalReplyNamespace();
    token = null;
    authContextUnavailable = true;
    window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
    if (next) throw new Error('Unable to safely persist the authentication session');
    // If the pending marker cannot be written, deletion can still persist logout.
    // Remove both credential copies, or report that logout was not persisted.
    try {
      localStorage.removeItem('fleet_hub_token');
      localStorage.removeItem(AUTH_CONTEXT_STORAGE_KEY);
      if (localStorage.getItem('fleet_hub_token') !== null || localStorage.getItem(AUTH_CONTEXT_STORAGE_KEY) !== null) {
        throw new Error('Logout not persisted');
      }
    } catch {
      window.dispatchEvent(new Event('fleet-hub-auth-storage-error'));
      throw new Error('Unable to safely persist sign out');
    }
  };
  // Publish the new opaque epoch before changing the bearer. A crash or blocked
  // write leaves a pending context that every tab and reload rejects.
  if (!epoch || !writeAuthContext({ epoch, state: 'pending' })) {
    failClosed();
    return;
  }
  clearOwnerReplyOperations();
  try {
    if (next) localStorage.setItem('fleet_hub_token', next);
    else localStorage.removeItem('fleet_hub_token');
  } catch {
    failClosed();
    return;
  }
  if (!writeAuthContext({ epoch, state: 'ready', credential: next })) {
    failClosed();
    return;
  }
  activeAuthEpoch = epoch;
  authContextUnavailable = false;
  requiresExplicitAuthentication = false;
  token = next;
  if (next) createOwnerReplySessionId(epoch);
  window.dispatchEvent(new Event('fleet-hub-auth-session-changed'));
}

export function getToken(): string | null {
  synchronizeAuthContext();
  return token;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  synchronizeAuthContext();
  const requestAuthGeneration = authGeneration;
  const requestAuthToken = token;
  const requestAuthEpoch = activeAuthEpoch;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const isCurrentContext = () => {
    const readable = synchronizeAuthContext();
    // A logged-out or failed-closed tab must still be able to log in. Anonymous
    // responses may return only while their captured context remains unchanged.
    return (readable || requestAuthToken === null) && requestAuthGeneration === authGeneration
      && requestAuthEpoch === activeAuthEpoch && requestAuthToken === token;
  };
  // Fence every response, even before the browser delivers a queued storage event.
  if (!isCurrentContext()) {
    throw new ApiError(res.ok ? 409 : res.status, 'Authentication context changed; reconcile the original operation');
  }
  if (res.status === 401 && requestAuthToken) {
    setToken(null);
    window.dispatchEvent(new Event('fleet-hub-unauthorized'));
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (typeof body.detail === 'string') {
        detail = body.detail;
      } else if (Array.isArray(body.detail)) {
        // FastAPI/Pydantic validation errors: a list of {loc, msg, ...} objects.
        detail = body.detail.map((e: { msg?: string }) => e.msg).filter(Boolean).join('; ') || detail;
      }
    } catch {
      // ignore non-JSON error bodies
    }
    if (!isCurrentContext()) throw new ApiError(res.status, 'Authentication context changed; reconcile the original operation');
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  const result = (await res.json()) as T;
  // Body decoding is another asynchronous boundary; no old-owner receipt may
  // reach its caller after a login change during decoding.
  if (!isCurrentContext()) throw new ApiError(409, 'Authentication context changed; reconcile the original operation');
  return result;
}

window.addEventListener('storage', (event) => {
  if (event.key === null || event.key === AUTH_CONTEXT_STORAGE_KEY || event.key === 'fleet_hub_token') {
    synchronizeAuthContext();
  }
});

export interface AppRow {
  id: string;
  name: string;
  slug: string;
  api_key_last4: string;
  is_active: boolean;
}

export interface UpdateManifest {
  app_id: string;
  platform: string;
  channel: string;
  artifact_id: string;
  package_name: string;
  version_code: number;
  version_name: string;
  min_supported_code: number;
  mandatory: boolean;
  changelog: string;
  artifact_url: string;
  sha256: string;
  signature: string;
}

export interface UpdateChannel {
  id: string;
  app_id: string;
  platform: string;
  channel: string;
  artifact_id: string;
  package_name: string;
  pinned_public_key_b64: string;
  mandatory_floor: number;
  has_release: boolean;
  created_at: string;
}

export interface UpdateChannelPage {
  items: UpdateChannel[];
  next_cursor: string | null;
}

export interface UpdatePublicationReceipt {
  id: string;
  version_code: number;
  manifest: UpdateManifest;
  reason: string;
  published_at: string;
}

export interface AppCreatedRow extends AppRow {
  api_key: string;
}

export interface CustomerRow {
  id: string;
  external_customer_id: string;
  last_event_type: string | null;
  tariff: string | null;
  renews_at: string | null;
}

export interface RevenueSnapshotData {
  metric: "latest_active_payment_snapshot";
  amounts_minor_by_currency: Record<string, string>;
  active_paid_subscriptions: number;
  missing_amount_subscriptions: number;
  missing_period_subscriptions: number;
  normalized_mrr_minor_by_currency: null;
  mrr_status: "billing_periods_unavailable";
}

export interface AnalyticsSummary {
  active_total: number;
  active_by_tariff: Record<string, number>;
  not_renewed: { customer_id: string; tariff: string | null; renews_at: string }[];
  not_renewed_count: number;
  churned_count: number;
  mrr_by_currency: Record<string, number>;
  revenue_snapshot?: RevenueSnapshotData | null;
}

export interface OwnerThreadSummary {
  id: string;
  subject: string;
  external_customer_id: string;
  is_open: boolean;
  created_at: string;
  updated_at: string;
}

export interface OwnerThreadListResponse {
  items: OwnerThreadSummary[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface OwnerMessageItem {
  id: string;
  sender: string;
  body: string;
  created_at: string;
}

export interface OwnerMessageListResponse {
  items: OwnerMessageItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface Tariff {
  id: string;
  code: string;
  name: string;
  price: number | null;
  currency: string;
  is_active: boolean;
}
